import PageHero from "../components/common/PageHero";
import Reveal from "../components/common/Reveal";
import useSeo from "../hooks/useSeo";

const H = ({ children }) => <h2 className="font-serif text-xl text-navy">{children}</h2>;
const H3 = ({ children }) => <h3 className="mt-6 text-sm font-semibold text-navy">{children}</h3>;
const P = ({ children }) => <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{children}</p>;
const Ext = ({ href, children }) => <a href={href} target="_blank" rel="noopener noreferrer" className="font-semibold text-navy underline decoration-gold/50 underline-offset-2 hover:text-gold">{children}</a>;

const BROWSER_GUIDES = [
  ["Google Chrome", "https://support.google.com/chrome/answer/95647"],
  ["Mozilla Firefox", "https://support.mozilla.org/kb/enable-and-disable-cookies-website-preferences"],
  ["Safari (Desktop)", "https://support.apple.com/guide/safari/manage-cookies-sfri11471"],
  ["Safari (Mobile)", "https://support.apple.com/en-gb/HT201265"],
  ["Microsoft Edge", "https://support.microsoft.com/microsoft-edge/delete-cookies-in-microsoft-edge-63947406"],
];

export default function CookiePolicy() {
  useSeo({ title: "Cookie Policy", description: "How Azimi & Co. Vision Recruitment uses cookies, the types of cookies we use, and your choices." });
  return (
    <>
      <PageHero overline="Legal" title="Cookie Policy" description="How we use cookies on the Azimi & Co. Vision Recruitment website, and the choices available to you." />
      <section className="py-20 lg:py-24" data-testid="cookie-policy-page">
        <div className="mx-auto max-w-4xl px-6 lg:px-8">
          <Reveal>
            <div className="rounded-2xl border border-border bg-white p-8 sm:p-10">
              <h1 className="font-serif text-2xl text-navy sm:text-3xl">Azimi &amp; Co. Vision Recruitment — Cookie Policy</h1>

              <div className="mt-10 space-y-10">
                <div>
                  <H>What Are Cookies?</H>
                  <P>A cookie is a small text file that a website asks your browser to store on your device when you visit, so that the site can remember information about you — such as your preferences or login details — on your next visit. Cookies do not harm your device.</P>
                  <P>You can normally adjust your browser settings to limit or block cookies. See 'Your Choices Regarding Cookies' below.</P>
                </div>

                <div>
                  <H>How We Use Cookies</H>
                  <P>We use cookies to help our website function correctly, to understand how visitors use our site so we can improve our services, and to provide live support functionality.</P>
                </div>

                <div>
                  <H>Types of Cookies We Use</H>
                  <H3>Strictly Necessary Cookies</H3>
                  <P>These cookies are essential for the website to function — for example, enabling secure areas, remembering your cookie consent choices, or allowing you to submit an enquiry or job application. These cannot be switched off, as the site would not work properly without them. They do not store personally identifiable information.</P>
                  <H3>Functional / Preference Cookies</H3>
                  <P>These remember choices you make on our site — such as display settings or preferences — so we can provide a more personalised experience on your return. If disabled, some features may not work as intended.</P>
                  <H3>Analytics &amp; Performance Cookies</H3>
                  <P>We use analytics tools (specifically PostHog) to understand how visitors navigate and use our website — for example, which pages are most visited and how visitors interact with our platform. This helps us improve our recruitment services.</P>
                  <H3>Live Support &amp; Interactive Widgets</H3>
                  <P>We use live chat tools (specifically Tawk.to) to provide real-time support and assistance to candidates and employers interacting with our platform. This tool may set operational cookies to manage active chat sessions.</P>
                  <H3>Session Cookies</H3>
                  <P>As you move through our site, certain information is temporarily stored so the site functions correctly during your visit. These are deleted automatically when you close your browser.</P>
                </div>

                <div>
                  <H>Third-Party Services</H>
                  <P>Where our website connects to third-party tools (such as PostHog for analytics or Tawk.to for live support), those providers may process data in accordance with their own privacy and platform policies. As our website evolves and we integrate new tools, this policy and our specific cookie list will be updated accordingly.</P>
                </div>

                <div>
                  <H>Your Choices Regarding Cookies</H>
                  <P>You can set your browser to warn you before accepting cookies, or to block them altogether. Each browser manages this slightly differently — consult your browser's Help menu for exact steps. If you disable cookies, some parts of our site (such as job applications or saved preferences) may not function correctly.</P>
                  <H3>Browser cookie settings guides:</H3>
                  <ul className="mt-3 list-disc space-y-1.5 pl-6 text-sm leading-relaxed text-muted-foreground" data-testid="browser-guides">
                    {BROWSER_GUIDES.map(([label, href]) => <li key={label}><Ext href={href}>{label}</Ext></li>)}
                  </ul>
                </div>

                <div>
                  <H>Changes to This Cookie Policy</H>
                  <P>We may update this Cookie Policy from time to time to reflect changes in the tools and cookies we use, or for legal and regulatory reasons. Please check back periodically for the latest version.</P>
                  <P>This Cookie Policy was last reviewed and updated on: 11 September 2026.</P>
                </div>

                <div>
                  <H>Contact Us</H>
                  <P>If you have any questions about our use of cookies, please contact us at:</P>
                  <P>Azimi &amp; Co. Vision Recruitment Ltd Dublin, Ireland &amp; London, United Kingdom</P>
                  <P>Email: <a href="mailto:info@azimivision.com" className="font-semibold text-navy hover:text-gold">info@azimivision.com</a> Phone: <a href="tel:+353899637844" className="font-semibold text-navy hover:text-gold">+353 89 963 7844</a> (Ireland) | <a href="tel:+447352757530" className="font-semibold text-navy hover:text-gold">+44 73 52 757530</a> (UK)</P>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
